import posthog from 'posthog-js';

interface ProductInfo {
  id: string;
  name: string;
  price: number;
  currency: string;
}

interface VariantInfo {
  size: string;
  price: number;
}

export function trackProductListViewed(products: ProductInfo[]) {
  posthog.capture('product_list_viewed', {
    products: products.map((p) => ({
      product_id: p.id,
      name: p.name,
      price: p.price,
      currency: p.currency,
    })),
    product_count: products.length,
  });
}

export function trackProductViewed(
  product: { id: string; name: string; category?: string },
  variant: VariantInfo,
  currency: string
) {
  posthog.capture('product_viewed', {
    product_id: product.id,
    product_name: product.name,
    category: product.category,
    variant: variant.size,
    price: variant.price,
    currency,
  });
}

export function trackProductAdded(
  product: { id: string; name: string },
  variant: VariantInfo,
  quantity: number,
  currency: string
) {
  posthog.capture('product_added', {
    product_id: product.id,
    product_name: product.name,
    variant: variant.size,
    price: variant.price,
    quantity,
    currency,
  });
}

export function trackCartViewed(items: { productId: string; name: string; quantity: number; price: number }[], currency: string) {
  const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  posthog.capture('cart_viewed', {
    item_count: items.length,
    cart_total: total,
    currency,
    items: items.map((i) => ({
      product_id: i.productId,
      name: i.name,
      quantity: i.quantity,
      price: i.price,
    })),
  });
}

export function trackCheckoutStarted(
  items: { productId: string; name: string; quantity: number; price: number }[],
  currency: string
) {
  const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  posthog.capture('checkout_started', {
    item_count: items.length,
    cart_total: total,
    currency,
    items: items.map((i) => ({
      product_id: i.productId,
      name: i.name,
      quantity: i.quantity,
      price: i.price,
    })),
  });
}

export function trackCheckoutButtonClicked(
  items: { productId: string; name: string; quantity: number; price: number }[],
  currency: string
) {
  const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  posthog.capture('checkout_button_clicked', {
    item_count: items.length,
    cart_total: total,
    currency,
    items: items.map((i) => ({
      product_id: i.productId,
      name: i.name,
      quantity: i.quantity,
      price: i.price,
    })),
  });
}

export function trackCheckoutRedirectedToStripe(
  currency: string,
  itemCount: number
) {
  posthog.capture('checkout_redirected_to_stripe', {
    currency,
    item_count: itemCount,
  });
}

export function trackOrderCompleted(sessionId: string | null, total: number, currency: string, itemCount: number) {
  posthog.capture('order_completed', {
    session_id: sessionId,
    total,
    currency,
    item_count: itemCount,
  });
}

export function trackSubscriptionStarted(
  product: { id: string; name: string },
  size: string,
  currency: string
) {
  posthog.capture('subscription_started', {
    product_id: product.id,
    product_name: product.name,
    variant: size,
    currency,
  });
}

export function trackSubscriptionCheckoutButtonClicked(
  product: { id: string; name: string },
  variant: { size: string; price: number },
  currency: string
) {
  posthog.capture('subscription_checkout_button_clicked', {
    product_id: product.id,
    product_name: product.name,
    variant: variant.size,
    price: variant.price,
    currency,
  });
}

export function trackSubscriptionRedirectedToStripe(
  product: { id: string; name: string },
  variant: { size: string; price: number },
  currency: string
) {
  posthog.capture('subscription_redirected_to_stripe', {
    product_id: product.id,
    product_name: product.name,
    variant: variant.size,
    price: variant.price,
    currency,
  });
}
